import { toast } from "react-toastify";
import { refreshPermissions } from "utils/permissionUtils";
import { isOrganizationDeletedResponse, persistLoginSession } from "utils/organizationAccess";

/**
 * After successful authentication, persist session and route to dashboard,
 * org picker, or the organization-deleted blocking screen.
 */
export function finishAuthenticatedLogin(
  responseData,
  { navigate, setToken, email, queryClient, successMessage, requiresPasswordChange }
) {
  if (responseData?.requiresOrgSelection) {
    sessionStorage.setItem(
      "pendingOrgSelection",
      JSON.stringify({
        memberships: responseData.memberships || [],
        email: responseData.email || email,
        requiresPasswordChange: Boolean(requiresPasswordChange),
      })
    );
    if (responseData?.token) {
      sessionStorage.setItem("pendingOrgSelectToken", responseData.token);
    }
    toast.info("Select an organization to continue");
    navigate("/select-organization", {
      state: {
        memberships: responseData.memberships || [],
        token: responseData.token,
        email: responseData.email || email,
        requiresPasswordChange: Boolean(requiresPasswordChange),
      },
    });
    return;
  }

  persistLoginSession(responseData, email, setToken);

  if (isOrganizationDeletedResponse(responseData)) {
    toast.info("Your organization has been deleted. Please contact support.");
    navigate("/organization-deleted");
    return;
  }

  refreshPermissions(responseData?.token || "");
  queryClient?.invalidateQueries?.();

  if (requiresPasswordChange) {
    toast.success(successMessage || "Organization selected");
    navigate("/first-login/set-password");
    return;
  }

  toast.success(successMessage || "Logged in successfully");
  // A referral partner never lands on the admin dashboard: their role's permission list is
  // empty, so every /admin route would refuse them. The portal guard re-checks server-side.
  const orgRole =
    responseData?.orgRole ?? responseData?.userDetail?.orgRole ?? responseData?.userDetail?.type;
  navigate(orgRole === "REFERRAL_USER" ? "/partner/dashboard" : "/admin/dashboard");
}

/**
 * After first-login OTP when password change is required.
 */
export function finishFirstLoginOtp(responseData, { navigate, setToken, email }) {
  if (responseData?.requiresOrgSelection) {
    sessionStorage.setItem(
      "pendingOrgSelection",
      JSON.stringify({
        memberships: responseData.memberships || [],
        email: responseData.email || email,
        requiresPasswordChange: true,
      })
    );
    if (responseData?.token) {
      sessionStorage.setItem("pendingOrgSelectToken", responseData.token);
    }
    navigate("/select-organization", {
      state: {
        memberships: responseData.memberships || [],
        token: responseData.token,
        email: responseData.email || email,
        requiresPasswordChange: true,
      },
    });
    return;
  }

  persistLoginSession(responseData, email, setToken);

  if (isOrganizationDeletedResponse(responseData)) {
    toast.info("Your organization has been deleted. Please contact support.");
    navigate("/organization-deleted");
    return;
  }

  toast.success("Email verified!");
  navigate("/first-login/set-password");
}
